import {
  IDeliveryCost,
  IDeliveryLocationInput,
  IDeliveryProductInput,
  EGPAY_ACCOUNT_TYPE,
  IFarmInputQuote,
  IGpayAccount,
  ILinkedGpayAccount,
} from 'constants/interfaces';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  apiCalculateDeliveryCost,
  apiFarmerLinkedAccounts,
  apiGetLegacyRestrictedAccounts,
} from 'constants/Api';
import {apiGetWalletCashflows} from 'constants/walletCashflowApi';
import type {
  IWalletCashflow,
  IWalletMonthlyBudget,
} from 'constants/walletCashflowTypes';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {formattedDepotAddress} from 'screens/Dashboard/Depots/DepotTableSetup';
import {Skeleton} from '@material-ui/lab';
import {
  _NumberWithSpaces,
  EInputOrderPaymentType,
  IlogisticsTypeId,
  VAT,
} from 'constants/index';

type TWalletWithCashflows = {
  wallet: ILinkedGpayAccount;
  cashflows: IWalletCashflow[];
};

type TWalletOption = {
  id: string;
  name: string;
  account?: IGpayAccount;
  availableBalance: number;
  approvedBudgetAmount: number;
  gpayAccountIdsByProductId: Record<string, string | number>;
  eligible: boolean;
  reason: string;
  cashflow?: IWalletCashflow;
  missingCategoryNames: string[];
  insufficientCategoryNames: string[];
};

type TQuoteCashflowCategoryTotal = {
  key: string;
  total: number;
  name: string;
  candidateKeys: string[];
  productIds: string[];
};

type TWalletBudgetCoverage = {
  cashflow?: IWalletCashflow;
  approvedBudgetAmount: number;
  coversQuote: boolean;
  missingCategoryNames: string[];
  insufficientCategoryNames: string[];
};

export const CreateOrderFromQuoteDialog = (props: {
  isOpen: boolean;
  quote?: IFarmInputQuote;
  onClose: () => any;
  onConfirm: (
    isVatExempt: boolean,
    logisticsTypeId: IlogisticsTypeId,
    logisticsCost: number,
    paymentOptions: {
      paymentType: EInputOrderPaymentType;
      gpayAccountIdsByProductId?: Record<string, string | number>;
      gpayTransactional?: boolean;
    },
    contactPersonName?: string,
    contactPersonPhoneNumber?: string,
    deliveryInstructions?: string,
  ) => any;
}) => {
  const [isVatExempt, setIsVatExempt] = useState(
    props.quote?.vatExempt ?? false,
  );
  const [logisticsTypeId, setLogisticsTypeId] = useState<IlogisticsTypeId>(1);
  const [logisticsCost, setLogisticsCost] = useState(0);
  const [promotedLogisticsCost, setPromotedLogisticsCost] = useState<number>();
  const [isCalculatingLogistics, setIsCalculatingLogistics] = useState(false);
  const [logisticsErrorMessage, setLogisticsErrorMessage] = useState<string>();
  const [contactPersonName, setContactPersonName] = useState<string>();
  const [contactPersonPhone, setContactPersonPhone] = useState<string>();
  const [deliveryInstructions, setDeliveryInstructions] = useState<string>();
  const [paymentType, setPaymentType] = useState<EInputOrderPaymentType>(
    EInputOrderPaymentType.INVOICE,
  );

  const [isRestrictedAccountEligible, setIsRestrictedAccountEligible] =
    useState(false);
  const [restrictedAccountItems, setRestrictedAccountItems] = useState<any[]>(
    [],
  );
  const [isCheckingRestrictedAccount, setIsCheckingRestrictedAccount] =
    useState(true);
  const [selectedRestrictedAccountId, setSelectedRestrictedAccountId] =
    useState<string | undefined>();

  const [selectedWalletId, setSelectedWalletId] = useState<string>();
  const [wallets, setWallets] = useState<TWalletWithCashflows[]>([]);
  const [isLoadingWallets, setIsLoadingWallets] = useState(false);
  const [walletErrorMessage, setWalletErrorMessage] = useState<string>();

  const quote = props.quote;
  const depot = quote?.depot;
  const farmer = props.quote?.farmer;
  const farm = farmer?.farm;

  useEffect(() => {
    setContactPersonName(props.quote?.contactPersonName);
    setContactPersonPhone(props.quote?.contactPersonPhoneNumber);
    setDeliveryInstructions(props.quote?.deliveryInstructions);
    if (props.quote?.logisticsCost)
      setLogisticsTypeId(IlogisticsTypeId.SUPPLIER_CUSTOM);
  }, [props.quote]);

  useEffect(() => {
    if (!props.isOpen) return;
    setPaymentType(EInputOrderPaymentType.INVOICE);
    setSelectedWalletId(undefined);
    setSelectedRestrictedAccountId(undefined);
  }, [props.isOpen, props.quote?.id]);

  useEffect(() => {
    let isMounted = true;
    if (!props.isOpen || !farmer?.id) {
      return () => {
        isMounted = false;
      };
    }

    setIsLoadingWallets(true);
    setWalletErrorMessage(undefined);

    apiFarmerLinkedAccounts(String(farmer.id))
      .then(unwrapLinkedAccounts)
      .then((linkedWallets) =>
        Promise.all(
          linkedWallets.map((wallet) =>
            apiGetWalletCashflows(String(wallet.id))
              .catch(() => [])
              .then((cashflows) => ({wallet, cashflows})),
          ),
        ),
      )
      .then((loadedWallets) => {
        if (isMounted) setWallets(loadedWallets);
      })
      .catch((err) => {
        console.error('Error loading farmer wallets', err);
        if (isMounted) {
          setWallets([]);
          setWalletErrorMessage('Unable to load linked wallets.');
        }
      })
      .finally(() => {
        if (isMounted) setIsLoadingWallets(false);
      });

    return () => {
      isMounted = false;
    };
  }, [props.isOpen, farmer?.id]);

  useEffect(() => {
    let isMounted = true;
    if (!props.isOpen || !farmer?.id || !quote) return;

    setIsCheckingRestrictedAccount(true);
    const subCategories: number[] = quote.lineItems
      .map((item) => {
        return Number((item.product as any).mainCategoryId);
      })
      .filter((id) => !!id && !isNaN(id));

    if (subCategories.length < 1) {
      setIsRestrictedAccountEligible(false);
      setRestrictedAccountItems([]);
      setIsCheckingRestrictedAccount(false);
      return;
    }

    apiGetLegacyRestrictedAccounts(subCategories, String(farmer.id))
      .then((result) => {
        if (!isMounted) return;
        if (Array.isArray(result) && result.length > 0) {
          const sum = result.reduce(
            (acc: number, item: any) => acc + (item.restictionAmount || 0),
            0,
          );
          if (sum > 0) {
            setIsRestrictedAccountEligible(true);
            setRestrictedAccountItems(result);
          } else {
            setIsRestrictedAccountEligible(false);
            setRestrictedAccountItems([]);
          }
        } else {
          setIsRestrictedAccountEligible(false);
          setRestrictedAccountItems([]);
        }
      })
      .catch((err) => {
        console.error('Error checking restricted account eligibility:', err);
        if (isMounted) {
          setIsRestrictedAccountEligible(false);
          setRestrictedAccountItems([]);
        }
      })
      .finally(() => {
        if (isMounted) setIsCheckingRestrictedAccount(false);
      });

    return () => {
      isMounted = false;
    };
  }, [props.isOpen, farmer?.id, quote]);

  const walletOptions = useMemo(
    () => wallets.map((wallet) => getWalletOption(wallet, quote, isVatExempt)),
    [wallets, quote, isVatExempt],
  );
  const selectedWallet = walletOptions.find(
    (wallet) => wallet.id === selectedWalletId,
  );

  const createDisabled =
    !logisticsTypeId ||
    isCalculatingLogistics ||
    !!logisticsErrorMessage ||
    isNaN(logisticsCost) ||
    (paymentType === EInputOrderPaymentType.WALLET &&
      (!selectedWallet || !selectedWallet.eligible) &&
      !selectedRestrictedAccountId);

  const setFreeDeliveryLogisticsCost = useCallback(() => {
    setLogisticsCost(0);
    setPromotedLogisticsCost(undefined);
  }, []);

  const setCustomDeliveryLogisticsCost = useCallback(() => {
    setLogisticsCost(props.quote?.logisticsCost || 0);
    setPromotedLogisticsCost(undefined);
  }, [props.quote]);

  useEffect(() => {
    console.log({
      logisticsCost,
      promotedLogisticsCost,
      logisticsTypeId,
      isCalculatingLogistics,
      logisticsErrorMessage,
      quote,
    });
  }, [logisticsCost, quote]);

  const calculateCourierGuyLogisticsCost = useCallback(() => {
    setIsCalculatingLogistics(true);
    Promise.resolve()
      .then(() => {
        if (!farmer?.address || !farmer?.farm?.latLong)
          throw Error('Farm address not available');
      })
      .then(() => {
        const depotLocation: IDeliveryLocationInput = {
          latitude: depot?.latitude.substring(0, 10) || '',
          longitude: depot?.longitude.substring(0, 10) || '',
        };
        const farmLocation: IDeliveryLocationInput = {
          latitude: farm?.latLong?.split(',')[0].substring(0, 10) || '',
          longitude: farm?.latLong?.split(',')[1].substring(0, 10) || '',
        };
        const products: IDeliveryProductInput[] =
          quote?.lineItems.map((line) => ({
            id: line.product.id,
            quantity: line.quantity,
          })) || [];
        return apiCalculateDeliveryCost(depotLocation, farmLocation, products);
      })
      .then((deliveryCost: IDeliveryCost) => {
        if (!deliveryCost.success) throw new Error(deliveryCost.message);
        if (deliveryCost.originalTotalDeliveryCost) {
          setLogisticsCost(deliveryCost.originalTotalDeliveryCost!);
          setPromotedLogisticsCost(deliveryCost.totalDeliveryCost!);
        } else {
          setLogisticsCost(deliveryCost.totalDeliveryCost!);
          setPromotedLogisticsCost(undefined);
        }
        setLogisticsErrorMessage(undefined);
      })
      .catch((err) => {
        console.warn('Error calculating courier guy logistics cost', err);
        setLogisticsErrorMessage(
          err.message ||
            'Error calculating courier cost. Please try again later.',
        );
      })
      .finally(() => setIsCalculatingLogistics(false));
  }, [props.quote?.farmer]);

  // calculate logistics cost
  useEffect(() => {
    switch (logisticsTypeId) {
      case IlogisticsTypeId.COLLECTION:
      case IlogisticsTypeId.FREE_DELIVERY:
        setFreeDeliveryLogisticsCost();
        return;
      case IlogisticsTypeId.COURIER_GUY_DELIVERY:
        calculateCourierGuyLogisticsCost();
        return;
      case IlogisticsTypeId.SUPPLIER_CUSTOM:
        setCustomDeliveryLogisticsCost();
        return;
      default:
        throw new Error(
          'Unhandled logistics type when calculating logistics cost',
        );
    }
  }, [logisticsTypeId]);

  const logisticsOptions = [
    {id: IlogisticsTypeId.COLLECTION, name: 'Collection'},
    {id: IlogisticsTypeId.FREE_DELIVERY, name: 'Free Delivery'},
    {id: IlogisticsTypeId.COURIER_GUY_DELIVERY, name: 'Courier Guy Delivery'},
    {id: IlogisticsTypeId.SUPPLIER_CUSTOM, name: 'Custom'},
  ];

  return (
    <Dialog
      className="quote-create-order-dialog"
      disableEscapeKeyDown
      open={props.isOpen}
      onClose={props.onClose}
    >
      <DialogTitle className="heading">
        Create Order (Quote {props.quote?.quoteNumber})
      </DialogTitle>
      <DialogContent className="body">
        <Box className={'create-quote-order-form'} marginBottom={2}>
          <LogisticsCostCard
            isLoading={isCalculatingLogistics}
            depotAddress={
              props.quote?.depot
                ? formattedDepotAddress(props.quote.depot)
                : undefined
            }
            farmAddress={props.quote?.farmer.address}
            estimatedCourierCost={logisticsCost}
            promotedCourierCost={promotedLogisticsCost}
            errorMessage={logisticsErrorMessage}
          />
          <Box marginBottom={1} />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={isVatExempt}
                onChange={(event, checked) => setIsVatExempt(checked)}
              />
            }
            label={<Typography variant={'body2'}>VAT exempt</Typography>}
          />
          <Box marginBottom={1} />
          <div className="delivery-section">
            <Box marginBottom={2} />
            <Box className="delivery-fields">
              <TextField
                className="input"
                label="Contact Person Name"
                variant="outlined"
                fullWidth
                type="text"
                size="small"
                value={contactPersonName}
                onChange={(event) => setContactPersonName(event.target.value)}
              />
              <Box marginBottom={2} />
              <TextField
                className="input"
                label="Contact Person Phone Number"
                variant="outlined"
                fullWidth
                type="text"
                size="small"
                value={contactPersonPhone}
                onChange={(event) => setContactPersonPhone(event.target.value)}
              />
              <Box marginBottom={2} />
              <TextField
                className="input"
                label="Delivery Instructions"
                variant="outlined"
                fullWidth
                type="text"
                size="small"
                multiline
                value={deliveryInstructions}
                onChange={(event) =>
                  setDeliveryInstructions(event.target.value)
                }
              />
            </Box>
          </div>
          <Box marginBottom={4} />
          <Autocomplete
            disableClearable
            options={logisticsOptions}
            getOptionLabel={(option) => option.name}
            size="small"
            value={logisticsOptions.find(
              (option) => option.id === logisticsTypeId,
            )}
            onChange={(event, value) => setLogisticsTypeId(value?.id)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Logistics"
                size="small"
                variant="outlined"
              />
            )}
          />
          {logisticsTypeId === IlogisticsTypeId.SUPPLIER_CUSTOM && (
            <>
              <Box marginBottom={2} />
              <TextField
                className="input"
                label="Custom Logistics Cost (R)"
                variant="outlined"
                fullWidth
                type="number"
                size="small"
                value={logisticsCost}
                onChange={(event) =>
                  setLogisticsCost(parseFloat(event.target.value))
                }
              />
            </>
          )}
          <Box marginBottom={3} />
          <Typography variant="subtitle2" style={{fontWeight: 'bold'}}>
            Payment method
          </Typography>
          <RadioGroup
            value={String(paymentType)}
            onChange={(event) => {
              const nextPaymentType = Number(
                event.target.value,
              ) as EInputOrderPaymentType;
              setPaymentType(nextPaymentType);
              if (nextPaymentType === EInputOrderPaymentType.INVOICE) {
                setSelectedWalletId(undefined);
                setSelectedRestrictedAccountId(undefined);
              }
            }}
          >
            <FormControlLabel
              value={String(EInputOrderPaymentType.INVOICE)}
              control={<Radio color="primary" />}
              label={<Typography variant="body2">Invoice</Typography>}
            />
            <FormControlLabel
              value={String(EInputOrderPaymentType.WALLET)}
              control={<Radio color="primary" />}
              label={
                <Typography variant="body2">
                  Wallet {isCheckingRestrictedAccount ? '(Checking...)' : ''}
                </Typography>
              }
            />
          </RadioGroup>

          {Number(paymentType) === EInputOrderPaymentType.WALLET && (
            <Box>
              <Typography
                variant="body2"
                style={{fontWeight: 'bold', marginTop: 16}}
              >
                Cashflow Wallets
              </Typography>
              <Box marginTop={1}>
                {isLoadingWallets ? (
                  <Box display="flex" alignItems="center">
                    <CircularProgress size={18} />
                    <Box marginRight={1} />
                    <Typography variant="body2">Loading wallets...</Typography>
                  </Box>
                ) : walletErrorMessage ? (
                  <Typography variant="body2" color="error">
                    {walletErrorMessage}
                  </Typography>
                ) : walletOptions.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No linked wallets found for this farmer.
                  </Typography>
                ) : (
                  walletOptions.map((wallet) => (
                    <Paper
                      key={wallet.id}
                      variant="outlined"
                      onClick={() => {
                        if (wallet.eligible) {
                          setSelectedWalletId(wallet.id);
                          setSelectedRestrictedAccountId(undefined);
                        }
                      }}
                      style={{
                        padding: 12,
                        marginTop: 8,
                        cursor: wallet.eligible ? 'pointer' : 'not-allowed',
                        opacity: wallet.eligible ? 1 : 0.55,
                        borderColor:
                          selectedWalletId === wallet.id
                            ? '#2f7d5f'
                            : undefined,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            style={{fontWeight: 'bold'}}
                          >
                            {wallet.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {wallet.account?.accountName || 'Wallet account'}
                            {wallet.account?.accountNumber
                              ? ` - ${wallet.account.accountNumber}`
                              : ''}
                          </Typography>
                        </Box>
                        <Radio
                          color="primary"
                          checked={selectedWalletId === wallet.id}
                          disabled={!wallet.eligible}
                        />
                      </Box>
                      <Box marginTop={1}>
                        <Typography variant="body2">
                          Available: R{' '}
                          {_NumberWithSpaces(wallet.availableBalance)}
                        </Typography>
                        <Typography variant="body2">
                          Approved budget cover: R{' '}
                          {_NumberWithSpaces(wallet.approvedBudgetAmount)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={wallet.eligible ? 'primary' : 'error'}
                        >
                          {wallet.reason}
                        </Typography>
                        {wallet.cashflow && (
                          <Typography
                            display="block"
                            variant="caption"
                            color="textSecondary"
                          >
                            Approved cashflow:{' '}
                            {wallet.cashflow.description ||
                              wallet.cashflow.creditApplicationRef
                                ?.applicationId ||
                              `#${wallet.cashflow.id}`}
                          </Typography>
                        )}
                        {wallet.missingCategoryNames.length > 0 && (
                          <Typography
                            display="block"
                            variant="caption"
                            color="textSecondary"
                          >
                            Missing: {wallet.missingCategoryNames.join(', ')}
                          </Typography>
                        )}
                        {wallet.insufficientCategoryNames.length > 0 && (
                          <Typography
                            display="block"
                            variant="caption"
                            color="textSecondary"
                          >
                            Low budget:{' '}
                            {wallet.insufficientCategoryNames.join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  ))
                )}
              </Box>

              <Typography
                variant="body2"
                style={{fontWeight: 'bold', marginTop: 16}}
              >
                Restricted Account Wallets
              </Typography>
              {isRestrictedAccountEligible ? (
                <Box marginTop={1}>
                  {restrictedAccountItems.map((item, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      onClick={() => {
                        setSelectedRestrictedAccountId(
                          String(item.account?.id),
                        );
                        setSelectedWalletId(undefined);
                      }}
                      style={{
                        cursor: 'pointer',
                        padding: 12,
                        marginTop: 8,
                        borderColor:
                          selectedRestrictedAccountId ===
                          String(item.account?.id)
                            ? '#2f7d5f'
                            : undefined,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            style={{fontWeight: 'bold'}}
                          >
                            Restricted Account
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {item.account?.accountType || 'Wallet Account'}{' '}
                            {item.account?.id ? `- ${item.account.id}` : ''}
                          </Typography>
                        </Box>
                        <Radio
                          color="primary"
                          checked={
                            selectedRestrictedAccountId ===
                            String(item.account?.id)
                          }
                        />
                      </Box>
                      <Box marginTop={1}>
                        <Typography variant="body2">
                          Available Amount: R{' '}
                          {_NumberWithSpaces(item.restictionAmount || 0)}
                        </Typography>
                        <Typography variant="caption" color="primary">
                          Eligible for checkout.
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box marginTop={1}>
                  <Typography variant="body2" color="textSecondary">
                    No restricted accounts found for these products.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button className="cancel-button" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          className="accept-button"
          color="primary"
          disabled={createDisabled}
          onClick={() => {
            props.onConfirm(
              isVatExempt,
              logisticsTypeId,
              isNaN(promotedLogisticsCost!)
                ? logisticsCost
                : promotedLogisticsCost!,
              {
                paymentType,
                ...(paymentType === EInputOrderPaymentType.WALLET
                  ? {
                      gpayAccountIdsByProductId: (() => {
                        const mapping: Record<string, string | number> = {};
                        quote?.lineItems.forEach((item) => {
                          if (selectedRestrictedAccountId) {
                            mapping[String(item.product.id)] =
                              selectedRestrictedAccountId;
                          } else if (selectedWalletId) {
                            const accountId = selectedWallet?.account?.id;
                            if (accountId) {
                              mapping[String(item.product.id)] = accountId;
                            }
                          }
                        });
                        return mapping;
                      })(),
                      gpayTransactional: !!selectedWalletId,
                    }
                  : {}),
              },
              contactPersonName,
              contactPersonPhone,
              deliveryInstructions,
            );
          }}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const unwrapLinkedAccounts = (response: any): ILinkedGpayAccount[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.getFarmerInputsLinkedAccounts))
    return response.getFarmerInputsLinkedAccounts;
  if (Array.isArray(response?.data?.getFarmerInputsLinkedAccounts))
    return response.data.getFarmerInputsLinkedAccounts;
  return [];
};

const getWalletOption = (
  walletWithCashflows: TWalletWithCashflows,
  quote?: IFarmInputQuote,
  isVatExempt?: boolean,
): TWalletOption => {
  const {wallet, cashflows} = walletWithCashflows;
  const account = getWalletPaymentAccount(wallet);
  const availableBalance = Number(account?.AvailableBalance || 0);
  const productTotal = getQuoteProductTotal(quote, isVatExempt);
  const hasAccount = !!account?.id;
  const hasBalance = productTotal > 0 && availableBalance >= productTotal;
  const budgetCoverage = getWalletBudgetCoverage(cashflows, quote, isVatExempt);
  const eligible = hasAccount && hasBalance && budgetCoverage.coversQuote;
  const gpayAccountIdsByProductId =
    eligible && account?.id
      ? getQuoteGpayAccountIdsByProductId(quote, account.id)
      : {};

  return {
    id: String(wallet.id),
    name: wallet.company?.name || account?.accountName || 'Wallet',
    account,
    availableBalance,
    approvedBudgetAmount: budgetCoverage.approvedBudgetAmount,
    gpayAccountIdsByProductId,
    eligible,
    reason: getWalletEligibilityReason({
      hasAccount,
      hasBalance,
      productTotal,
      budgetCoverage,
    }),
    cashflow: budgetCoverage.cashflow,
    missingCategoryNames: budgetCoverage.missingCategoryNames,
    insufficientCategoryNames: budgetCoverage.insufficientCategoryNames,
  };
};

const getWalletPaymentAccount = (
  wallet: ILinkedGpayAccount,
): IGpayAccount | undefined =>
  wallet.accounts?.find(
    (account) => account.accountType === EGPAY_ACCOUNT_TYPE.TPFM,
  ) || wallet.accounts?.[0];

const getQuoteProductTotal = (quote?: IFarmInputQuote, isVatExempt?: boolean) =>
  (quote?.lineItems || []).reduce(
    (total, line) => total + getQuoteLineTotal(line, isVatExempt),
    0,
  );

const getQuoteCategoryTotals = (
  quote?: IFarmInputQuote,
  isVatExempt?: boolean,
) => {
  const totals = new Map<string, TQuoteCashflowCategoryTotal>();

  (quote?.lineItems || []).forEach((line) => {
    const candidateKeys = getQuoteLineCategoryCandidates(line)
      .map(normalizeCategory)
      .filter(Boolean);
    const key =
      candidateKeys.find((candidate) => /[a-z]/.test(candidate)) ||
      candidateKeys[0];
    if (!key) return;
    const lineTotal = getQuoteLineTotal(line, isVatExempt);
    const existingTotal = totals.get(key);

    totals.set(key, {
      key,
      candidateKeys: Array.from(
        new Set([...(existingTotal?.candidateKeys || []), ...candidateKeys]),
      ),
      total: (existingTotal?.total || 0) + lineTotal,
      name:
        existingTotal?.name ||
        line.product.subCategoryName ||
        line.product.category ||
        line.product.name,
      productIds: Array.from(
        new Set([
          ...(existingTotal?.productIds || []),
          String(line.product.id),
        ]),
      ),
    });
  });

  return Array.from(totals.values());
};

const getQuoteGpayAccountIdsByProductId = (
  quote: IFarmInputQuote | undefined,
  accountId: string | number,
) =>
  (quote?.lineItems || []).reduce<Record<string, string | number>>(
    (productAccounts, line) => {
      productAccounts[String(line.product.id)] = accountId;
      return productAccounts;
    },
    {},
  );

const getQuoteLineTotal = (
  line: IFarmInputQuote['lineItems'][number],
  isVatExempt?: boolean,
) =>
  Number(line.price || 0) *
  Number(line.quantity || 0) *
  (isVatExempt ? 1 : 1 + VAT);

const getQuoteLineCategoryCandidates = (
  line: IFarmInputQuote['lineItems'][number],
) => {
  const product = line.product as any;
  return [
    product.category,
    product.subCategoryName,
    product.subCategoryId,
    product.mainCategoryName,
    product.mainCategoryId,
    product.categoryId,
  ].filter(Boolean);
};

const getWalletBudgetCoverage = (
  cashflows: IWalletCashflow[],
  quote?: IFarmInputQuote,
  isVatExempt?: boolean,
): TWalletBudgetCoverage => {
  const categoryTotals = getQuoteCategoryTotals(quote, isVatExempt);
  const emptyCoverage: TWalletBudgetCoverage = {
    approvedBudgetAmount: 0,
    coversQuote: false,
    missingCategoryNames: categoryTotals.map((category) => category.name),
    insufficientCategoryNames: [],
  };
  if (!categoryTotals.length) return emptyCoverage;

  return sortCashflowsByNewest(cashflows).reduce<TWalletBudgetCoverage>(
    (bestCoverage, cashflow) => {
      const usedBudgetAmounts = new Map<string, number>();
      const budgets = getCashflowPeriodBudgets(cashflow);
      const coverage = categoryTotals.reduce<TWalletBudgetCoverage>(
        (cashflowCoverage, category) => {
          const budget = budgets.find((budgetLine) =>
            budgetMatchesCategory(budgetLine, category),
          );

          if (!budget) {
            cashflowCoverage.missingCategoryNames.push(category.name);
            return cashflowCoverage;
          }

          const usedBudgetAmount = usedBudgetAmounts.get(budget.id) || 0;
          const remainingAmount = Math.max(
            Number(budget.remainingAmount || 0) - usedBudgetAmount,
            0,
          );
          const coveredAmount = Math.min(remainingAmount, category.total);
          usedBudgetAmounts.set(budget.id, usedBudgetAmount + category.total);

          cashflowCoverage.cashflow = cashflow;
          cashflowCoverage.approvedBudgetAmount += coveredAmount;
          if (remainingAmount < category.total)
            cashflowCoverage.insufficientCategoryNames.push(category.name);
          return cashflowCoverage;
        },
        {
          approvedBudgetAmount: 0,
          coversQuote: false,
          missingCategoryNames: [],
          insufficientCategoryNames: [],
        },
      );

      coverage.coversQuote =
        coverage.missingCategoryNames.length === 0 &&
        coverage.insufficientCategoryNames.length === 0;

      if (coverage.coversQuote && !bestCoverage.coversQuote) return coverage;
      if (
        coverage.coversQuote === bestCoverage.coversQuote &&
        coverage.approvedBudgetAmount > bestCoverage.approvedBudgetAmount
      )
        return coverage;
      return bestCoverage;
    },
    emptyCoverage,
  );
};

const getWalletEligibilityReason = (params: {
  hasAccount: boolean;
  hasBalance: boolean;
  productTotal: number;
  budgetCoverage: TWalletBudgetCoverage;
}) => {
  if (!params.hasAccount)
    return 'No wallet payment account is available for this wallet.';
  if (params.productTotal <= 0) return 'Quote has no products to pay for.';
  if (!params.hasBalance) return 'Wallet balance is below the quote total.';
  if (!params.budgetCoverage.cashflow)
    return 'No approved cashflow budget found for this checkout month.';
  if (params.budgetCoverage.missingCategoryNames.length)
    return `No approved cashflow category for ${params.budgetCoverage.missingCategoryNames.join(
      ', ',
    )}.`;
  if (params.budgetCoverage.insufficientCategoryNames.length)
    return `Approved cashflow budget is too low for ${params.budgetCoverage.insufficientCategoryNames.join(
      ', ',
    )}.`;
  return 'Eligible against approved cashflow budget.';
};

const getCashflowPeriodBudgets = (cashflow: IWalletCashflow) => {
  const checkoutPeriod = getCheckoutPeriod();
  return (cashflow.budgets || []).filter(
    (budget) =>
      Number(budget.periodYear) === checkoutPeriod.year &&
      Number(budget.periodMonth) === checkoutPeriod.month &&
      budget.spendCategory?.usageKind !== 'WITHDRAWAL',
  );
};

const getCheckoutPeriod = () => {
  const date = new Date();
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
  };
};

const budgetMatchesCategory = (
  budget: IWalletMonthlyBudget,
  category: TQuoteCashflowCategoryTotal,
) => {
  const budgetCategoryKeys = [
    budget.spendCategory?.id,
    budget.spendCategory?.code,
    budget.spendCategory?.label,
  ]
    .map(normalizeCategory)
    .filter(Boolean);

  return category.candidateKeys.some((categoryKey) =>
    budgetCategoryKeys.some(
      (budgetCategoryKey) =>
        budgetCategoryKey === categoryKey ||
        trimTrailingPlural(budgetCategoryKey) ===
          trimTrailingPlural(categoryKey),
    ),
  );
};

const sortCashflowsByNewest = (cashflows: IWalletCashflow[]) =>
  [...cashflows].sort(
    (a, b) => getCashflowSortValue(b) - getCashflowSortValue(a),
  );

const getCashflowSortValue = (cashflow: IWalletCashflow) => {
  const linkedAtTime = new Date(
    cashflow.linkedAt || cashflow.periodStart || '',
  ).getTime();
  if (!isNaN(linkedAtTime)) return linkedAtTime;
  return Number(cashflow.id) || 0;
};

const normalizeCategory = (value?: string | number) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const trimTrailingPlural = (value: string) => value.replace(/s$/, '');

const LogisticsCostCard = (props: {
  isLoading: boolean;
  depotAddress?: string;
  farmAddress?: string;
  estimatedCourierCost: number;
  promotedCourierCost?: number;
  errorMessage?: string;
}) => {
  if (props.isLoading) return <Skeleton height={200} variant={'rect'} />;
  const shouldShowPromotedCost =
    !isNaN(props.promotedCourierCost!) &&
    props.promotedCourierCost !== props.estimatedCourierCost;
  return (
    <Paper variant={'outlined'}>
      <Box padding={2}>
        <Box marginBottom={1}>
          <Typography
            variant={'subtitle2'}
            style={{
              fontWeight: 'bold',
              ...(shouldShowPromotedCost && {
                textDecoration: 'line-through',
                textDecorationThickness: 1,
              }),
            }}
          >
            Estimated Courier Cost:{' '}
            {props.errorMessage || isNaN(props.estimatedCourierCost)
              ? ''
              : 'R ' + _NumberWithSpaces(props.estimatedCourierCost)}
          </Typography>
          {shouldShowPromotedCost && (
            <Typography variant={'subtitle2'} style={{fontWeight: 'bold'}}>
              Promoted Courier Cost:{' '}
              {props.errorMessage
                ? ''
                : 'R ' + _NumberWithSpaces(props.promotedCourierCost)}
            </Typography>
          )}
        </Box>
        <Box marginBottom={1}>
          <Typography variant={'body2'}>Depot Address:</Typography>
          <Typography variant={'body2'}>{props.depotAddress}</Typography>
        </Box>
        <Box marginBottom={1}>
          <Typography variant={'body2'}>Farm Address:</Typography>
          <Typography variant={'body2'}>{props.farmAddress}</Typography>
        </Box>
        {props.errorMessage && (
          <Box>
            <Typography variant={'body2'} color={'error'}>
              Error: {props.errorMessage}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
